import * as React from "react"
import * as MK from "../utils/mapkit/utils"
import * as Zoom from "../utils/mapkit/zoom"
import { Accomodation, MapState } from "../utils/mapkit/types"
import * as ReactDOM from "react-dom"

import { getAccommodationAnnotation, getClusterAnnotation } from "../utils/mapkit/annotations"
import _ from 'underscore'
import * as Immutable from 'immutable'

type Props = {
  clusters: Immutable.List<Accomodation>
  map_state: MapState
  setZoom: (_: number) => void
  setInitialMapState: (_: MapState) => void
  setInitialMapStateIsDefault: (_: boolean) => void
  highlightedAccommodation: Accomodation
  setBounds: (_: string) => void
  getMapsSnippetLink: (_: Accomodation) => string
  setMapState: (_: MapState) => void
  baseLink: string
  default_bounds: string
  className: string
}

type State = {
  highlightMarker: mapkit.Annotation
  accommodation_markers: Immutable.Map<number, mapkit.Annotation>
  accommodation_clusters: Immutable.List<mapkit.Annotation>
}

declare var window: any

export default class AppleMaps extends React.Component<Props, State> {
  map : mapkit.Map
  initialListener : any

  constructor(props, context) {
    super(props, context)
    this.state = {
      accommodation_markers: Immutable.Map<number, mapkit.Annotation>(),
      highlightMarker: null,
      accommodation_clusters: Immutable.List<mapkit.Annotation>()
    }

  }
  componentDidMount() {
    this.initializeAppleMaps();
  }
  componentWillUnmount() {
    this.map.destroy()
  }

  componentDidUpdate(prevProps : Props, _prevState : State) {
    if (!_.isEqual(prevProps.clusters, this.props.clusters)) {
      this.updateMarkers();
    }

    if (!MK.mapStateEqual(prevProps.map_state, this.props.map_state)) {
      this.updateMapState();
    }

    if (!prevProps.highlightedAccommodation && this.props.highlightedAccommodation) {
      this.highlightedAccommodation(this.props.highlightedAccommodation);
    }

    if (!this.props.highlightedAccommodation && this.state.highlightMarker) {
      this.removeHighlighterMarker();
    }
  }

  showAllOnMap() {
    return this.fitDefaultBounds();
  }

  async initializeAppleMaps() {
    await MK.initialize();
    this.map = new mapkit.Map(ReactDOM.findDOMNode(this.refs.map) as Element, {
      isRotationEnabled: false
    })

    if (!this.props.map_state) {
      this.fitDefaultBounds();
      this.props.setInitialMapStateIsDefault(true)
    } else {
      this.applyMapState(this.props.map_state);
      this.props.setInitialMapStateIsDefault(false)
    }

    this.map.addEventListener("select", (event) => {
      const annotation = event.annotation
      const cluster = annotation.data.accommodation
      if (!cluster || cluster.type !== 'cluster' ) {
        return;
      }
      this.map.region = MK.getBoundingRegionForCluster(cluster);
    })

    this.map.addEventListener("region-change-end", () => {
      this.boundsChanged();
    });

    this.props.setInitialMapState(MK.mapToMapState(this.map))
    this.boundsChanged();
  }

  fitDefaultBounds() {
    this.map.region = this.defaultLatLngBounds().toCoordinateRegion();
  }

  latLngBoundsFor(bounds) {
    return new mapkit.BoundingRegion(bounds[2], bounds[3], bounds[0], bounds[1])
  }

  defaultLatLngBounds() {
    return this.latLngBoundsFor(this.props.default_bounds);
  }

  applyMapState(map_state: MapState) {
    this.map.region = MK.mapStateToCoordinateRegion(this.map, map_state);
  }

  boundsChanged() {
    this.props.setZoom(Zoom.getZoom(this.map));
    this.props.setMapState(MK.mapToMapState(this.map));
    this.props.setBounds(MK.boundsToGMString(this.map.region.toBoundingRegion()));
  }

  updateMarkers() {
    const accommodation_markers = Immutable.Map<number, mapkit.Annotation>();
    const accommodation_clusters = Immutable.List<mapkit.Annotation>();

    const annotations = Immutable.List<mapkit.Annotation>(this.props.clusters.map(cluster => {
      const marker = cluster.type === 'cluster' ?
        this.getClusterMarkerFor(cluster) :
        this.getAccommodationMarkerFor(cluster);

      if (cluster.type !== 'cluster') {
        accommodation_markers[parseInt(cluster.id)] = marker;
      }
      if (cluster.type === 'cluster') {
        accommodation_clusters.push(marker);
      }
      return marker
    }))

    const diff = MK.annotationDiff(Immutable.List(this.map.annotations), Immutable.List(annotations))

    this.map.removeAnnotations(diff.remove.toArray())
    this.map.addAnnotations(diff.add.toArray());

    this.setState({ ...this.state, accommodation_markers, accommodation_clusters });
  }

  getClusterMarkerFor(cluster) {
    return getClusterAnnotation(cluster)
  }

  getAccommodationMarkerFor(accommodation) {
    return getAccommodationAnnotation(
      accommodation,
      this.props.getMapsSnippetLink,
      '',
      true
    )
  }

  updateMapState() {
    if (!this.props.map_state || !this.map || MK.mapStateEqual(this.props.map_state, MK.mapToMapState(this.map))) {
      return;
    }

    this.applyMapState(this.props.map_state);
  }

  currentMarkerFor(accommodation) {
    return this.state.accommodation_markers[parseInt(accommodation.id)];
  }

  highlightedAccommodation(accommodation) {
    const hl_marker = getAccommodationAnnotation(
      accommodation,
      this.props.getMapsSnippetLink,
      'hover',
      false
    )
    this.map.addAnnotation(hl_marker)
    this.setState({ ...this.state, highlightMarker: hl_marker })

    const marker = this.currentMarkerFor(accommodation);
    if (marker) {
      marker.visible = false;
    }
  }

  removeHighlighterMarker() {
    const marker = this.currentMarkerFor(this.state.highlightMarker.data.accommodation);

    if (marker) { marker.visible = true; }
    this.map.removeAnnotation(this.state.highlightMarker);
    this.setState({ ...this.state, highlightMarker: null })
  }

  render() {
    return <div className={this.props.className} ref='map'></div>;
  }
}
